// frontend/src/features/livestock/photoMetadata.ts
// Photo metadata read in the browser before upload. The EXIF reader is
// deliberately small (decision 12.1 in docs/plans/2026-10-13-photo-inbox.md):
// JPEG only, and only the tags needed for the capture date.

import type { ClientTakenAtSource } from '@/types/timeline';

export interface CaptureDate {
  /** Wall-clock time the camera wrote, "YYYY-MM-DDTHH:MM:SS". */
  local: string;
  /** That time's UTC offset, "+HH:MM" or "-HH:MM", or null when the photo doesn't say. */
  offset: string | null;
  /** The EXIF tag the date came from. */
  source: 'DateTimeOriginal' | 'CreateDate';
  /** SubSecTimeOriginal digits (fractions of the DateTimeOriginal second), when the photo has them. */
  subsec?: string;
}

/** EXIF lives in APP1 near the start of the file; this is plenty and avoids reading 10 MB photos. */
const HEAD_BYTES = 128 * 1024;

const TAG_EXIF_IFD = 0x8769;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME_DIGITIZED = 0x9004;
const TAG_OFFSET_TIME_ORIGINAL = 0x9011;
const TAG_OFFSET_TIME_DIGITIZED = 0x9012;
const TAG_SUB_SEC_TIME_ORIGINAL = 0x9291;

/** "Exif\0\0", the identifier that starts an Exif APP1 payload (XMP also uses APP1). */
const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

/** IFD entries are 12 bytes: tag, type, count, then the value or a pointer to it. */
const ENTRY_SIZE = 12;

/** Dates are 20 bytes and offsets 7; a longer string means the block is corrupt. */
const MAX_ASCII_LENGTH = 64;

interface Tiff {
  /** The TIFF block. EXIF pointers are offsets from its start. */
  view: DataView;
  little: boolean;
}

interface Entry {
  count: number;
  /** Position of the entry's 4-byte value-or-pointer field within the TIFF block. */
  field: number;
}

type Ifd = Map<number, Entry>;

/** Walks the JPEG segments up to the image data and returns the Exif APP1's TIFF block. */
function findTiff(view: DataView): Tiff | null {
  const end = view.byteLength;
  if (end < 4 || view.getUint16(0) !== 0xffd8) return null;
  let pos = 2;
  while (pos + 4 <= end) {
    const marker = view.getUint16(pos);
    // Metadata segments come before start-of-scan (0xFFDA); nothing useful follows it.
    if (marker >> 8 !== 0xff || marker === 0xffda || marker === 0xffd9) return null;
    const length = view.getUint16(pos + 2);
    if (length < 2) return null;
    // A segment cut short by the end of what was read is used as far as it goes.
    const segmentEnd = Math.min(end, pos + 2 + length);
    const start = pos + 4 + EXIF_HEADER.length;
    if (
      marker === 0xffe1 &&
      start <= segmentEnd &&
      EXIF_HEADER.every((b, i) => view.getUint8(pos + 4 + i) === b)
    ) {
      return openTiff(new DataView(view.buffer, view.byteOffset + start, segmentEnd - start));
    }
    pos += 2 + length;
  }
  return null;
}

/** Checks the TIFF header: byte order "II" or "MM", then 42. */
function openTiff(view: DataView): Tiff | null {
  if (view.byteLength < 8) return null;
  const order = view.getUint16(0);
  if (order !== 0x4949 && order !== 0x4d4d) return null;
  const little = order === 0x4949;
  if (view.getUint16(2, little) !== 0x002a) return null;
  return { view, little };
}

/** Reads the IFD at `offset`, or null if it doesn't fit inside the TIFF block. */
function readIfd(tiff: Tiff, offset: number): Ifd | null {
  const { view, little } = tiff;
  if (offset < 8 || offset + 2 > view.byteLength) return null;
  const count = view.getUint16(offset, little);
  if (offset + 2 + count * ENTRY_SIZE > view.byteLength) return null;
  const entries: Ifd = new Map();
  for (let i = 0; i < count; i++) {
    const at = offset + 2 + i * ENTRY_SIZE;
    entries.set(view.getUint16(at, little), {
      count: view.getUint32(at + 4, little),
      field: at + 8,
    });
  }
  return entries;
}

interface ExifBlock {
  tiff: Tiff;
  ifd0: Ifd;
  ifd0Offset: number;
}

/**
 * Follows a pointer tag in IFD0 to its sub-IFD (the Exif IFD). Empty when the
 * tag is absent; null when the pointer is broken (out of bounds, or back at IFD0).
 */
function readSubIfd({ tiff, ifd0, ifd0Offset }: ExifBlock, tag: number): Ifd | null {
  const pointer = ifd0.get(tag);
  if (!pointer) return new Map();
  const offset = tiff.view.getUint32(pointer.field, tiff.little);
  if (offset === ifd0Offset) return null;
  return readIfd(tiff, offset);
}

function readAscii(tiff: Tiff, entry: Entry | undefined): string | null {
  if (!entry || entry.count > MAX_ASCII_LENGTH) return null;
  // Values of up to 4 bytes sit in the entry itself; longer ones are behind a pointer.
  const start = entry.count > 4 ? tiff.view.getUint32(entry.field, tiff.little) : entry.field;
  if (start + entry.count > tiff.view.byteLength) return null;
  let text = '';
  for (let i = 0; i < entry.count; i++) {
    const code = tiff.view.getUint8(start + i);
    if (code === 0) break;
    text += String.fromCharCode(code);
  }
  return text;
}

const EXIF_DATE = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2}) *$/;
const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;
const OFFSET = /^[+-](\d{2}):(\d{2})$/;
const SUBSEC = /^(\d{1,9}) *$/;

/** Year, month, day, hour, minute, second from a date regex match, or null if they aren't a real date and time. */
function dateParts(match: RegExpExecArray | null): number[] | null {
  if (!match) return null;
  const parts = match.slice(1).map(Number);
  const [y = 0, mo = 0, d = 0, h = 0, mi = 0, s = 0] = parts;
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  if (y < 1 || mo < 1 || mo > 12 || d < 1 || d > daysInMonth || h > 23 || mi > 59 || s > 59) {
    return null;
  }
  return parts;
}

/** "YYYY:MM:DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS", or null if it isn't a real date and time. */
function parseExifDate(text: string | null): string | null {
  const match = text ? EXIF_DATE.exec(text) : null;
  if (!match || !dateParts(match)) return null;
  const [, year, month, day, hour, minute, second] = match;
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/** Keeps "+HH:MM" / "-HH:MM" offsets within ±14:00; anything else is null. */
function parseOffset(text: string | null): string | null {
  const match = text ? OFFSET.exec(text) : null;
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (minutes > 59 || hours * 60 + minutes > 14 * 60) return null;
  return match[0];
}

/** One to nine digits, without the trailing spaces some cameras pad with; anything else is null. */
function parseSubsec(text: string | null): string | null {
  const match = text ? SUBSEC.exec(text) : null;
  return match?.[1] ?? null;
}

/** Reads the start of the file and returns its EXIF IFD0, if it has a usable one. */
async function readExifBlock(file: Blob): Promise<ExifBlock | null> {
  let head: ArrayBuffer;
  try {
    head = await file.slice(0, HEAD_BYTES).arrayBuffer();
  } catch {
    // A picked file can become unreadable (moved, or iCloud download failed); treat it as bare.
    return null;
  }
  const tiff = findTiff(new DataView(head));
  if (!tiff) return null;
  const ifd0Offset = tiff.view.getUint32(4, tiff.little);
  const ifd0 = readIfd(tiff, ifd0Offset);
  return ifd0 ? { tiff, ifd0, ifd0Offset } : null;
}

/** Date tags in the Exif IFD, in the order they are tried, each with its offset tag. */
const DATE_TAGS = [
  { source: 'DateTimeOriginal', date: TAG_DATE_TIME_ORIGINAL, offset: TAG_OFFSET_TIME_ORIGINAL },
  { source: 'CreateDate', date: TAG_DATE_TIME_DIGITIZED, offset: TAG_OFFSET_TIME_DIGITIZED },
] as const;

/**
 * Reads when a JPEG was taken from its EXIF block: DateTimeOriginal, else
 * CreateDate (DateTimeDigitized), each with its matching offset tag. The IFD0
 * DateTime is never used, because it is the time the file was last changed.
 * Only a DateTimeOriginal date carries SubSecTimeOriginal. Returns null for
 * non-JPEGs, missing or invalid dates and malformed data; it never throws.
 * No plausibility checks (dates in the future, before digital cameras): callers apply those.
 */
export async function readCaptureDate(file: Blob): Promise<CaptureDate | null> {
  const block = await readExifBlock(file);
  if (!block) return null;
  const exif = readSubIfd(block, TAG_EXIF_IFD);
  if (!exif) return null;

  const { tiff } = block;
  for (const tags of DATE_TAGS) {
    const local = parseExifDate(readAscii(tiff, exif.get(tags.date)));
    if (!local) continue;
    const capture: CaptureDate = {
      local,
      offset: parseOffset(readAscii(tiff, exif.get(tags.offset))),
      source: tags.source,
    };
    if (tags.source === 'DateTimeOriginal') {
      const subsec = parseSubsec(readAscii(tiff, exif.get(TAG_SUB_SEC_TIME_ORIGINAL)));
      if (subsec) capture.subsec = subsec;
    }
    return capture;
  }
  return null;
}

/** A capture date the entry form can use: the moment, and the source to send with it. */
export interface CapturePrefill {
  date: Date;
  source: ClientTakenAtSource;
}

/** Photos whose own clock says earlier than this come from cameras that were never set (plan 6.3). */
const EARLIEST_LOCAL = '1990-01-01T00:00:00';

/** Nothing is taken tomorrow; this allows for a device clock that is a little behind (plan 6.3). */
const MAX_AHEAD_MS = 24 * 60 * 60 * 1000;

/**
 * Turns a readCaptureDate result into the date to prefill, applying the bounds
 * from section 6.3 of docs/plans/2026-10-13-photo-inbox.md. With an offset the
 * moment is local + offset (source 'exif'). Without one (or with an offset that
 * isn't valid), local is read on this device's clock ('exif_no_tz'). Returns
 * null, so the form keeps its own default, when there is no date, when the
 * photo's own date is before 1990-01-01, or when the moment is more than 24
 * hours after `now`.
 */
export function captureDateToPrefill(
  capture: CaptureDate | null,
  now: Date
): CapturePrefill | null {
  if (!capture || capture.local < EARLIEST_LOCAL) return null;
  const parts = dateParts(LOCAL_DATE.exec(capture.local));
  if (!parts) return null;
  const [y = 0, mo = 0, d = 0, h = 0, mi = 0, s = 0] = parts;

  const offset = parseOffset(capture.offset);
  const date = offset ? new Date(`${capture.local}${offset}`) : new Date(y, mo - 1, d, h, mi, s);
  if (Number.isNaN(date.getTime()) || date.getTime() > now.getTime() + MAX_AHEAD_MS) return null;
  return { date, source: offset ? 'exif' : 'exif_no_tz' };
}

/**
 * SHA-256 of the whole file as lowercase hex. crypto.subtle only exists in a
 * secure context (HTTPS or localhost), so this rejects on plain-HTTP LAN addresses.
 */
export async function hashFile(file: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The identity the inbox deduplicates photos by (`content_hash`, 64 lowercase
 * hex). Today the SHA-256 of the picked file. If the device spike shows iOS
 * gives different bytes each time the same photo is picked, this becomes an
 * EXIF identity key (DateTimeOriginal + SubSecTimeOriginal + offset +
 * dimensions, hashed to the same format); the backend accepts either
 * (plan 8.2 and Task 3). Every caller goes through here, so that switch is one change.
 */
export function computeContentHash(file: File): Promise<string> {
  return hashFile(file);
}

// ---------------------------------------------------------------------------
// Preparing a photo for upload (plan sections 3 and 8.2)
// ---------------------------------------------------------------------------

/** Long edge, in pixels, above which a photo is shrunk before upload. */
export const UPLOAD_MAX_EDGE = 4096;

/** Size above which a photo is re-encoded before upload (the server takes 10 MB). */
export const UPLOAD_MAX_BYTES = 9 * 1024 * 1024;

const JPEG_QUALITY = 0.85;
/** Second try when the first encode is still over UPLOAD_MAX_BYTES. */
const JPEG_QUALITY_SMALLER = 0.75;

const TAG_ORIENTATION = 0x0112;

/** Formats the server takes as they are. */
type AcceptedFormat = 'jpeg' | 'png' | 'webp';
type ImageFormat = AcceptedFormat | 'heic' | 'avif' | 'gif' | 'unknown';

interface Size {
  width: number;
  height: number;
}

/** Why a photo can't be sent: a format the browser can't turn into JPEG, or too big to shrink here. */
export type PrepareFailure = 'unsupported' | 'too_large';

export class PhotoPrepareError extends Error {
  readonly reason: PrepareFailure;

  constructor(reason: PrepareFailure, message: string) {
    super(message);
    this.name = 'PhotoPrepareError';
    this.reason = reason;
  }
}

export interface PreparedPhoto {
  /** The original file when it fits the upload limits, otherwise a JPEG made from it. */
  blob: Blob;
  /** Name to send: the original's, or with a .jpg extension for a converted copy. */
  filename: string;
  /** True when `blob` is a converted or shrunk copy. */
  resized: boolean;
  /** Capture date read from the ORIGINAL file; a copy made on a canvas has no EXIF. */
  capture: CaptureDate | null;
  /** The original file's lastModified, ms since 1970. */
  lastModified: number;
}

/** ISO-BMFF brands of HEIF files (what iPhones store photos as). */
const HEIF_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1']);

/** File-type signatures checked against the first bytes. */
function sniffFormat(head: Uint8Array): ImageFormat {
  const ascii = (from: number, to: number): string =>
    String.fromCharCode(...head.subarray(from, Math.min(to, head.length)));
  if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'jpeg';
  if (ascii(0, 8) === '\x89PNG\r\n\x1a\n') return 'png';
  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'webp';
  if (ascii(0, 4) === 'GIF8') return 'gif';
  if (ascii(4, 8) === 'ftyp') {
    const brand = ascii(8, 12);
    if (brand === 'avif' || brand === 'avis') return 'avif';
    if (HEIF_BRANDS.has(brand)) return 'heic';
  }
  return 'unknown';
}

/** Reads a file in chunks on demand, so a JPEG's segment headers can be walked without reading it all. */
class ChunkReader {
  private buffer = new Uint8Array(0);
  private start = 0;

  constructor(
    private readonly file: Blob,
    private readonly chunk = 64 * 1024
  ) {}

  /** `length` bytes at `pos` (fewer at the end of the file). */
  async bytes(pos: number, length: number): Promise<Uint8Array> {
    const end = pos + length;
    if (pos < this.start || end > this.start + this.buffer.length) {
      this.start = pos;
      this.buffer = new Uint8Array(
        await this.file.slice(pos, pos + Math.max(length, this.chunk)).arrayBuffer()
      );
    }
    return this.buffer.subarray(pos - this.start, end - this.start);
  }
}

/** SOFn markers (C0-CF except DHT C4, JPG C8 and DAC CC) carry the frame size. */
function isStartOfFrame(marker: number): boolean {
  return marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
}

/** Most segments walked before giving up and decoding instead. */
const MAX_SEGMENTS = 64;

/** Stored width and height from the JPEG's frame header, or null if it can't be found. */
async function readJpegFrameSize(reader: ChunkReader): Promise<Size | null> {
  let pos = 2;
  for (let walked = 0; walked < MAX_SEGMENTS; walked++) {
    const header = await reader.bytes(pos, 4);
    if (header.length < 4 || header[0] !== 0xff) return null;
    const marker = header[1] ?? 0;
    if (marker === 0xff) {
      pos += 1; // fill byte before a marker
      continue;
    }
    if (marker === 0xda || marker === 0xd9) return null; // image data before any frame header
    const length = ((header[2] ?? 0) << 8) | (header[3] ?? 0);
    if (length < 2) return null;
    if (isStartOfFrame(marker)) {
      const frame = await reader.bytes(pos + 4, 5);
      if (frame.length < 5) return null;
      const height = ((frame[1] ?? 0) << 8) | (frame[2] ?? 0);
      const width = ((frame[3] ?? 0) << 8) | (frame[4] ?? 0);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    pos += 2 + length;
  }
  return null;
}

/** IHDR is always the first PNG chunk: width and height as big-endian 32-bit values. */
function readPngSize(head: Uint8Array): Size | null {
  if (head.length < 24) return null;
  const view = new DataView(head.buffer, head.byteOffset, head.byteLength);
  if (String.fromCharCode(...head.subarray(12, 16)) !== 'IHDR') return null;
  const width = view.getUint32(16);
  const height = view.getUint32(20);
  return width > 0 && height > 0 ? { width, height } : null;
}

/** EXIF orientation (1-8), or 1 when the JPEG doesn't say. */
async function readOrientation(file: Blob): Promise<number> {
  const block = await readExifBlock(file);
  const entry = block?.ifd0.get(TAG_ORIENTATION);
  if (!block || !entry) return 1;
  const value = block.tiff.view.getUint16(entry.field, block.tiff.little);
  return value >= 1 && value <= 8 ? value : 1;
}

/**
 * Format and displayed size (after EXIF orientation) from the file's header,
 * without decoding it. Size is null when the header can't be read.
 */
async function readImageInfo(file: Blob): Promise<{ format: ImageFormat; size: Size | null }> {
  const reader = new ChunkReader(file);
  let head: Uint8Array;
  try {
    head = await reader.bytes(0, 32);
  } catch {
    return { format: 'unknown', size: null };
  }
  const format = sniffFormat(head);
  if (format === 'png') return { format, size: readPngSize(head) };
  if (format !== 'jpeg') return { format, size: null };

  const stored = await readJpegFrameSize(reader).catch(() => null);
  if (!stored) return { format, size: null };
  // Orientations 5 to 8 turn the picture a quarter turn, so it displays the other way round.
  const turned = (await readOrientation(file)) >= 5;
  return { format, size: turned ? { width: stored.height, height: stored.width } : stored };
}

/** `size` shrunk so its long edge is at most `edge`; never enlarged. */
function fitLongEdge(size: Size, edge: number): Size {
  const scale = Math.min(1, edge / Math.max(size.width, size.height));
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

/** Size from a full decode, for when the header couldn't be read; null if the browser can't decode it. */
async function decodedSize(file: Blob): Promise<Size | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

/** createImageBitmap, or null when the browser rejects the options as invalid (TypeError). */
async function decodeWith(file: Blob, options: ImageBitmapOptions): Promise<ImageBitmap | null> {
  try {
    return await createImageBitmap(file, options);
  } catch (error) {
    if (error instanceof TypeError) return null;
    throw error;
  }
}

/**
 * Decodes straight to `target` where the browser supports createImageBitmap's
 * resize options. A browser that rejects them as invalid (TypeError) is asked
 * again without imageOrientation (the option older engines lack), then for a
 * plain full-size decode; one that ignores them returns full size too.
 */
async function decodeAtSize(file: Blob, target: Size | null): Promise<ImageBitmap> {
  if (!target) return createImageBitmap(file);
  const resize: ImageBitmapOptions = {
    resizeWidth: target.width,
    resizeHeight: target.height,
    resizeQuality: 'high',
  };
  return (
    (await decodeWith(file, { ...resize, imageOrientation: 'from-image' })) ??
    (await decodeWith(file, resize)) ??
    createImageBitmap(file)
  );
}

function toJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The photo could not be encoded'))),
      'image/jpeg',
      quality
    );
  });
}

/**
 * Draws the photo at `target` (or at its own size, capped at UPLOAD_MAX_EDGE)
 * and encodes a JPEG, at a lower quality if the first is over UPLOAD_MAX_BYTES.
 * The bitmap is closed as soon as it is drawn, so the decoded photo and the
 * encoder's memory aren't both held, and the canvas's pixels are released
 * before returning: iOS caps canvas memory per page and the queue prepares
 * photos back to back.
 */
async function encodeJpeg(file: Blob, target: Size | null): Promise<Blob> {
  const bitmap = await decodeAtSize(file, target);
  const canvas = document.createElement('canvas');
  let open = true;
  const closeBitmap = (): void => {
    if (open) bitmap.close();
    open = false;
  };
  try {
    const exact = target && bitmap.width === target.width && bitmap.height === target.height;
    const size = exact ? target : fitLongEdge(bitmap, UPLOAD_MAX_EDGE);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    // JPEG has no transparency; without a fill, transparent PNG areas turn black.
    context.fillStyle = 'white';
    context.fillRect(0, 0, size.width, size.height);
    context.drawImage(bitmap, 0, 0, size.width, size.height);
    closeBitmap();
    const blob = await toJpeg(canvas, JPEG_QUALITY);
    return blob.size > UPLOAD_MAX_BYTES ? await toJpeg(canvas, JPEG_QUALITY_SMALLER) : blob;
  } finally {
    closeBitmap();
    canvas.width = 0;
    canvas.height = 0;
  }
}

function isAccepted(format: ImageFormat): format is AcceptedFormat {
  return format === 'jpeg' || format === 'png' || format === 'webp';
}

/** "HEIC" for the message, from the bytes or else the file's type; null when neither says. */
function formatName(format: ImageFormat, file: File): string | null {
  if (format !== 'unknown') return format.toUpperCase();
  const subtype = /^image\/([a-z0-9.+-]+)$/i.exec(file.type)?.[1];
  return subtype ? subtype.toUpperCase() : null;
}

function jpegName(name: string): string {
  const stem = name.replace(/\.[^./\\]*$/, '');
  return `${stem || 'photo'}.jpg`;
}

/**
 * Gets a picked photo ready to upload (plan section 3). A JPEG, PNG or WebP
 * with a long edge of at most 4096 px and at most 9 MB is returned untouched,
 * so the server reads its EXIF and nothing is lost to a re-encode. Its size
 * comes from the header (JPEG frame header with EXIF orientation, PNG IHDR),
 * falling back to a decode only when that can't be read. Anything else is
 * drawn at most 4096 px on the long edge and encoded as JPEG q0.85 (q0.75 if
 * still over 9 MB). The capture date and lastModified always come from the
 * original file.
 *
 * Rejects with PhotoPrepareError: 'unsupported' for a format the server
 * doesn't take and the browser can't decode (HEIC outside Safari), 'too_large'
 * when a photo that needs shrinking can't be decoded (a 48 MP photo can run a
 * phone out of memory).
 */
export async function prepareForUpload(file: File): Promise<PreparedPhoto> {
  const [info, capture] = await Promise.all([readImageInfo(file), readCaptureDate(file)]);
  const original = { capture, lastModified: file.lastModified };

  let size = info.size;
  if (isAccepted(info.format)) {
    size ??= await decodedSize(file);
    const fits = !size || Math.max(size.width, size.height) <= UPLOAD_MAX_EDGE;
    // A photo the browser can't measure is sent as it is when small enough; the server decides.
    if (fits && file.size <= UPLOAD_MAX_BYTES) {
      return { ...original, blob: file, filename: file.name || 'photo', resized: false };
    }
  }

  let blob: Blob;
  try {
    blob = await encodeJpeg(file, size ? fitLongEdge(size, UPLOAD_MAX_EDGE) : null);
  } catch {
    if (isAccepted(info.format)) {
      throw new PhotoPrepareError('too_large', 'Too large to resize on this device');
    }
    const name = formatName(info.format, file);
    throw new PhotoPrepareError('unsupported', `Format not supported${name ? ` (${name})` : ''}`);
  }
  return { ...original, blob, filename: jpegName(file.name), resized: true };
}
